import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleCount'];

  // --- storage helpers (safe) ---
  storageKey = 'ol_cart_count_v1';

  // Throttle: don't hit cart API more than once per 3 seconds (prevents slowness from tab switches / rapid events)
  syncThrottleMs = 3000;
  lastSyncTime = 0;

  readCachedCount() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      const n = parseInt(String(raw ?? '0'), 10);
      return Number.isFinite(n) ? n : null;
    } catch (_) {
      return null;
    }
  }

  writeCachedCount(n) {
    try {
      sessionStorage.setItem(this.storageKey, String(n));
    } catch (_) {
      // ignore (some sandboxed contexts/extensions can block storage)
    }
  }

  get currentCartCount() {
    const el = this.refs.cartBubbleCount;
    const raw = (el?.textContent ?? '0').trim();
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }

  set currentCartCount(value) {
    const safe = Number.isFinite(value) ? value : 0;

    this.refs.cartBubbleCount.textContent = safe < 100 ? String(safe) : '99+';

    // Always visible (even at 0)
    this.refs.cartBubble.classList.remove('visually-hidden', 'hidden');
    this.refs.cartBubbleCount.classList.remove('visually-hidden', 'hidden');

    // Some Horizon styles hide unless this class exists
    this.classList.add('header-actions__cart-icon--has-cart');

    // cache it so back/forward can paint instantly
    this.writeCachedCount(safe);
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.cartBubble || !this.refs.cartBubbleCount) return;

    this.forceVisible();

    // ✅ 1) Paint instantly from cache (or 0) — no network, no delay
    const cached = this.readCachedCount();
    if (cached !== null) {
      this.currentCartCount = cached;
    } else {
      this.currentCartCount = 0;
    }

    // ✅ 2) Sync from cart API only AFTER the page has painted (keeps load/refresh fast)
    const runSync = () => this.syncFromCartEndpoint(false);
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(runSync, { timeout: 500 });
    } else {
      setTimeout(runSync, 300);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    // ✅ BFCache/back-forward restore
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  forceVisible = () => {
    this.refs.cartBubble?.classList.remove('visually-hidden', 'hidden');
    this.refs.cartBubbleCount?.classList.remove('visually-hidden', 'hidden');

    this.refs.cartBubbleCount?.style.removeProperty('display');
    this.refs.cartBubble?.style.removeProperty('display');

    this.classList.add('header-actions__cart-icon--has-cart');

    const trigger = this.closest(
      '[data-testid="cart-drawer-trigger"], [data-testid="cart-icon"], a[href="/cart"], .header-actions__action'
    );

    if (trigger) {
      trigger.classList.remove('hidden', 'visually-hidden');
      trigger.style.display = '';
      trigger.style.visibility = 'visible';
      trigger.style.opacity = '1';
    }
  };

  onPageShow = (e) => {
    // Paint cache immediately so back/forward feels instant
    const cached = this.readCachedCount();
    if (cached !== null) this.currentCartCount = cached;
    else this.currentCartCount = 0;

    // Sync in background so we don't block the page
    if (e?.persisted) setTimeout(() => this.syncFromCartEndpoint(false), 100);
  };

  onVisibilityChange = () => {
    // Only restore from cache when tab becomes visible — do NOT fetch cart (was causing slowness when switching tabs)
    if (document.visibilityState === 'visible') {
      const cached = this.readCachedCount();
      if (cached !== null) this.currentCartCount = cached;
      else this.currentCartCount = 0;
    }
  };

  syncFromCartEndpoint = async (animate = false) => {
    const now = Date.now();
    if (now - this.lastSyncTime < this.syncThrottleMs) {
      // Recently synced — just show cached count, don't hit the API again
      const cached = this.readCachedCount();
      if (cached !== null) this.currentCartCount = cached;
      else this.currentCartCount = 0;
      this.forceVisible();
      return;
    }
    this.lastSyncTime = now;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s max wait
      const res = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        this.currentCartCount = 0;
        this.forceVisible();
        return;
      }

      const cart = await res.json();
      const count = Number(cart?.item_count ?? 0);

      // Only update DOM if different (reduces stutter)
      if (count !== this.currentCartCount) {
        await this.renderCartBubble(count, false, animate);
      } else {
        // still ensure it stays visible + cached
        this.currentCartCount = count;
      }
    } catch (_) {
      // On network/parse error, still show 0 and keep icon visible
      this.currentCartCount = 0;
      this.forceVisible();
    }
  };

  onCartUpdate = (event) => {
    const data = event?.detail?.data || {};
    const itemCount = Number(data.itemCount ?? data.item_count ?? 0);
    const comingFromProductForm = data.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm, true);
  };

  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    if (!this.refs.cartBubble || !this.refs.cartBubbleCount) return;

    this.forceVisible();

    const rawNext = comingFromProductForm
      ? this.currentCartCount + Number(itemCount || 0)
      : Number(itemCount ?? 0);
    const next = Number.isFinite(rawNext) ? rawNext : 0;

    // If no actual change, still ensure visible (e.g. stay at 0)
    if (next === this.currentCartCount) {
      this.currentCartCount = next;
      return;
    }

    // Update text (this also caches)
    this.currentCartCount = next;

    // Animate (optional)
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', animate);

    if (!animate) return;

    try {
      await onAnimationEnd(this.refs.cartBubble);
    } catch (_) {}

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
